import { listOfflineSales, removeOfflineSale, updateOfflineSale } from "~/utils/offline-pos"
import { useOfflinePosConnectivity } from "~/composables/useOfflinePosConnectivity"

const CONNECTIVITY_CHECK_INTERVAL_MS = 60 * 1000

export default defineNuxtPlugin(async () => {
	const online = useOfflinePosConnectivity()
	const { data: auth, status } = useAuth()
	let syncing = false
	let connectivityCheck = 0
	const updateConnectivity = async () => {
		const check = ++connectivityCheck
		if (!navigator.onLine) {
			online.value = false
			return false
		}

		const controller = new AbortController()
		const timeout = window.setTimeout(() => controller.abort(), 5000)
		try {
			const response = await fetch("/api/health", {
				cache: "no-store",
				signal: controller.signal,
			})
			const body = response.ok ? await response.json().catch(() => null) : null
			if (check === connectivityCheck) {
				online.value = response.ok && body?.success === true
			}
		} catch (_) {
			if (check === connectivityCheck) online.value = false
		} finally {
			window.clearTimeout(timeout)
		}
		return online.value
	}

	await updateConnectivity()

	const sync = async () => {
		if (
			!online.value ||
			status.value !== "authenticated" ||
			!auth.value?._id ||
			syncing
		) return
		syncing = true
		try {
			for (const sale of await listOfflineSales()) {
				if (
					sale.status === "needs_review" ||
					String(sale.cashier_id || "") !== String(auth.value._id)
				) continue
				try {
					const response = await $fetch("/api/sale/sync-offline", {
						method: "POST",
						body: sale,
					})
					if (response?.status === "synced" || response?.status === "already_synced") {
						await removeOfflineSale(sale.offline_sale_id)
					} else {
						await updateOfflineSale({
							...sale,
							status: "needs_review",
							sync_result: response,
						})
					}
				} catch (error) {
					const statusCode = error?.statusCode || error?.response?.status
					if (statusCode && statusCode !== 401 && statusCode !== 403 && statusCode < 500) {
						await updateOfflineSale({
							...sale,
							status: "needs_review",
							sync_error: error.statusMessage || error.message,
						})
					}
					else if (statusCode !== 401 && statusCode !== 403) {
						console.warn("Offline sale sync attempt failed; it will retry", error)
					}
					break
				}
			}
		} finally {
			syncing = false
		}
	}

	const refreshAndSync = async () => {
		if (await updateConnectivity()) await sync()
	}
	window.addEventListener("online", refreshAndSync)
	window.addEventListener("offline", updateConnectivity)
	window.addEventListener("focus", refreshAndSync)
	setInterval(() => {
		if (document.visibilityState !== "visible") return
		refreshAndSync().catch((error) =>
			console.warn("Offline POS sync retry failed", error)
		)
	}, CONNECTIVITY_CHECK_INTERVAL_MS)
	watch(status, (value) => {
		if (value === "authenticated") {
			sync().catch((error) => console.warn("Offline POS sync failed", error))
		}
	}, { immediate: true })
})
